import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  Destroyable,
  isSomething,
  Rental,
  SaveForm,
  selectChannelById,
  selectRentalEntities,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import {
  BookingFormulaAppliedSchedule,
  BookingFormulaItem,
  BookingFormulaBaseType,
  BookingFormulaRangeType,
  BookingFormulaValidityCriteria,
  BookingFormulaResponse,
  formulaAppliedMonths,
  isFormulasMonthOverlap,
  ToggleBookingFormulaStatus,
  selectAllBookingFormulas,
  UpdateBookingFormula,
  CreateBookingFormula,
  getFormulaScheduling,
  selectChannelsForFormulas,
  getAppliedMonths,
  isBookingFormulaBaseCustomizable,
} from '@tokeet-frontend/booking-formula'
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import * as R from 'ramda'
import { switchMap, map } from 'rxjs/operators'
import { combineLatest } from 'rxjs'
import { percent, positive } from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import * as lodash from 'lodash'
import * as moment from 'moment'
import { selectLinkedRentalsForChannel } from '@tv3/store/connection/connection.selectors'
import { asLocalDate, asUTCEpoch } from '@tokeet-frontend/tv3-platform'

@Component({
  selector: 'app-booking-formula-dialog',
  host: { class: 'modal-content' },
  templateUrl: './booking-formula-dialog.component.html',
  styleUrls: ['./booking-formula-dialog.component.scss'],
})
export class BookingFormulaDialogComponent extends Destroyable implements OnInit {
  baseTypes = [
    { label: $localize`Gross Booking Total`, value: BookingFormulaBaseType.BookingTotal },
    { label: $localize`Guest Payout`, value: BookingFormulaBaseType.GuestPayout },
  ]

  rangeTypeOptions: { label: string; value: BookingFormulaRangeType }[] = [
    { label: $localize`Month Range`, value: 'month' },
    { label: $localize`Date Range`, value: 'date' },
  ]

  validityCriteriaOptions: { label: string; value: BookingFormulaValidityCriteria }[] = [
    { label: $localize`Booked Date`, value: 'booked_date' },
    { label: $localize`Booking Created Date`, value: 'created_date' },
  ]

  isBaseTypeCustomizable = false

  private readonly startMonthValidators = [
    Validators.required,
    (ctrl: AbstractControl) => {
      const v = ctrl.value
      const end = this.form?.get('end_month').value
      if (!lodash.isNumber(v) || !lodash.isNumber(end)) {
        return
      }
      if (v > end) {
        return { max: true }
      }
    },
  ]

  private readonly endMonthValidators = [
    Validators.required,
    (ctrl: AbstractControl) => {
      const v = ctrl.value
      const start = this.form?.get('start_month').value
      if (!lodash.isNumber(v) || !lodash.isNumber(start)) {
        return
      }
      if (v < start) {
        return { min: true }
      }
    },
  ]

  form = this.fb.group(
    {
      base: [BookingFormulaBaseType.BookingTotal, [Validators.required]],
      name: ['', [Validators.required]],
      formula: this.fb.array([]),
      rentals: [[]],
      channel_id: ['', [Validators.required]],
      range_type: ['month' as BookingFormulaRangeType],
      validity_criteria: ['booked_date' as BookingFormulaValidityCriteria],
      // Month-range fields (used when range_type = 'month')
      start_month: [undefined, this.startMonthValidators],
      end_month: [undefined, this.endMonthValidators],
      // Date-range fields (used when range_type = 'date') — stored as Date objects for mat-datepicker
      start_date: [null as Date | null],
      end_date: [null as Date | null],
      status: [true],
      cumulative: [true],
    },
    {
      validators: [
        (form: FormGroup) => {
          const { start_month, end_month, range_type, start_date, end_date, rentals } = form.getRawValue()
          const startMonthCtrl = form.get('start_month')
          const startDateCtrl = form.get('start_date')

          this.setControlError(startMonthCtrl, 'overlap', false)
          this.setControlError(startDateCtrl, 'overlap', false)

          let effectiveStartMonth: number
          let effectiveEndMonth: number
          let dateRange: { start_date?: number; end_date?: number }

          if (range_type === 'date') {
            if (!start_date || !end_date) {
              return
            }
            effectiveStartMonth = moment(start_date).month()
            effectiveEndMonth = moment(end_date).month()
            dateRange = {
              start_date: asUTCEpoch(start_date, 'start'),
              end_date: asUTCEpoch(end_date, 'end'),
            }
          } else {
            if (!lodash.isNumber(start_month) || !lodash.isNumber(end_month)) {
              return
            }
            effectiveStartMonth = start_month
            effectiveEndMonth = end_month
          }

          const currentAppliedMonths = formulaAppliedMonths(effectiveStartMonth, effectiveEndMonth)
          // Only formulas of the same range_type can conflict — cross-type is always allowed
          const overlap = isFormulasMonthOverlap(this.appliedMonths, rentals, currentAppliedMonths, range_type, dateRange)

          if (overlap) {
            this.setControlError(range_type === 'date' ? startDateCtrl : startMonthCtrl, 'overlap', true)
            return { overlap: true }
          }
        },
      ],
    }
  )

  get formulaItems() {
    return this.form.get('formula') as FormArray
  }

  get rentalIds() {
    return this.form.get('rentals').value || []
  }

  get formula() {
    return this.data.formula
  }

  get isDateRange() {
    return this.form.get('range_type').value === 'date'
  }

  rentals: Rental[] = []
  rentalsById: { [key: string]: Rental } = {}
  months = moment.months().map((m, index) => ({ label: m, value: index }))

  /** Minimum selectable date for the date-range picker */
  minDate = moment().subtract(10, 'years').toDate()
  /** Maximum selectable date for the date-range picker */
  maxDate = moment().add(10, 'years').toDate()

  appliedMonths: BookingFormulaAppliedSchedule[] = []

  channels: { id: string; name: string }[] = []

  constructor(
    private store: Store<any>,
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<BookingFormulaDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      channel?: { id: string; name: string }
      formula?: BookingFormulaResponse
    }
  ) {
    super()
  }

  ngOnInit() {
    const formula = this.data.formula
    const channelId$ = this.form.get('channel_id').valueChanges

    // Update month validators when range_type changes
    this.form
      .get('range_type')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((rangeType: BookingFormulaRangeType) => {
        this.setSchedulingValidators(rangeType)
      })

    combineLatest([
      this.store
        .pipe(select(selectAllBookingFormulas))
        .pipe(map((items) => (!this.data.channel ? items : items.filter((t) => t.channel_id == this.data.channel.id)))),
      this.form
        .get('channel_id')
        .valueChanges.pipe(switchMap((id) => this.store.pipe(select(selectChannelById, { id })))),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([formulas, channel]) => {
        this.isBaseTypeCustomizable = isBookingFormulaBaseCustomizable(channel?.name)
        formulas = formulas.filter((f) => f.pkey !== formula?.pkey && channel.id === f.channel_id)
        this.appliedMonths = getAppliedMonths(formulas.filter((f) => f.pkey !== formula?.pkey))
      })

    channelId$
      .pipe(
        switchMap((channelId) =>
          this.store.pipe(
            select(selectLinkedRentalsForChannel(channelId)),
            map((items) => lodash.sortBy(items, (t) => lodash.toLower(t.name)))
          )
        ),
        untilDestroy(this)
      )
      .subscribe((rentals) => {
        this.rentals = rentals
      })

    this.formulaItems.clear()
    if (isSomething(formula)) {
      const range = getFormulaScheduling(formula)
      const rangeType = range.range_type as BookingFormulaRangeType

      this.form.patchValue({
        base: formula.base || BookingFormulaBaseType.BookingTotal,
        name: formula.name,
        status: !!formula.status,
        cumulative: !!formula.cumulative,
        rentals: formula.rentals || [],
        channel_id: formula.channel_id,
        range_type: rangeType,
        validity_criteria: formula.validity_criteria || 'booked_date',
        start_month: range.start_month,
        end_month: range.end_month,
        // Stored epochs are UTC day boundaries; render the UTC calendar date in the
        // picker (asLocalDate) so a save/reopen cycle doesn't drift a day for non-UTC users
        start_date: rangeType === 'date' && range.start_date ? asLocalDate(range.start_date, false) : null,
        end_date: rangeType === 'date' && range.end_date ? asLocalDate(range.end_date, false) : null,
      })
      R.map((t: BookingFormulaItem) => this.formulaItems.push(this.createFormula(t)), formula.formula)
    } else if (this.data.channel) {
      this.form.patchValue({ channel_id: this.data.channel.id })
    }

    this.store.pipe(select(selectRentalEntities), untilDestroy(this)).subscribe((res) => {
      this.rentalsById = res
    })

    this.store
      .pipe(select(selectChannelsForFormulas))
      .pipe(untilDestroy(this))
      .subscribe((channels) => {
        this.channels = channels
      })
  }

  getRentalsView(rentalIds: string[]) {
    return R.values(R.pick(rentalIds, this.rentalsById))
      .map((r) => r.name)
      .join(', ')
  }

  private setControlError(ctrl: AbstractControl, key: string, enabled: boolean) {
    const errors = enabled ? { ...(ctrl.errors || {}), [key]: true } : lodash.omit({ ...(ctrl.errors || {}) }, key)
    ctrl.setErrors(lodash.isEmpty(errors) ? null : errors)
  }

  private setSchedulingValidators(rangeType: BookingFormulaRangeType) {
    const startMonthCtrl = this.form.get('start_month')
    const endMonthCtrl = this.form.get('end_month')
    const startDateCtrl = this.form.get('start_date')
    const endDateCtrl = this.form.get('end_date')

    if (rangeType === 'date') {
      startMonthCtrl.clearValidators()
      endMonthCtrl.clearValidators()
      startDateCtrl.setValidators([Validators.required])
      endDateCtrl.setValidators([Validators.required])
    } else {
      startMonthCtrl.setValidators(this.startMonthValidators)
      endMonthCtrl.setValidators(this.endMonthValidators)
      startDateCtrl.clearValidators()
      endDateCtrl.clearValidators()
    }
    startMonthCtrl.updateValueAndValidity()
    endMonthCtrl.updateValueAndValidity()
    startDateCtrl.updateValueAndValidity()
    endDateCtrl.updateValueAndValidity()
  }

  createFormula(formula?: BookingFormulaItem) {
    const form = this.fb.group({
      amount: [R.pathOr(undefined, ['amount'], formula), [Validators.required]],
      name: [R.pathOr(undefined, ['name'], formula), [Validators.required]],
      type: [R.pathOr('percent', ['type'], formula)],
      operation: [R.pathOr('add', ['operation'], formula)],
    })

    const amountCtrl = form.get('amount')
    form.get('type').valueChanges.subscribe((type) => {
      amountCtrl.setValidators([Validators.required, type === 'percent' ? percent() : positive()])
      amountCtrl.updateValueAndValidity()
    })

    return form
  }

  addFormula(formula?: BookingFormulaItem) {
    this.formulaItems.push(this.createFormula(formula))
  }

  removeFormula(index: number) {
    this.formulaItems.removeAt(index)
  }

  close() {
    this.dialogRef.close()
  }

  onToggleStatus(status: boolean) {
    if (this.formula.pkey) {
      this.store.dispatch(ToggleBookingFormulaStatus({ id: this.formula.pkey, status }))
    }
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { start_month, end_month, range_type, start_date, end_date, validity_criteria, ...others } =
      form.getRawValue()

    let scheduling: BookingFormulaResponse['scheduling']

    if (range_type === 'date' && start_date && end_date) {
      scheduling = {
        start_month: null,
        end_month: null,
        range_type: 'date',
        start_date: asUTCEpoch(start_date, 'start'), // unix epoch seconds
        end_date: asUTCEpoch(end_date, 'end'),
      }
    } else {
      scheduling = {
        start_month,
        end_month,
        range_type: 'month',
        start_date: null,
        end_date: null,
      }
    }

    const payload = {
      ...others,
      status: others.status ? 1 : 0,
      cumulative: others.cumulative ? 1 : 0,
      validity_criteria,
      scheduling,
    }

    if (this.formula?.pkey) {
      this.store.dispatch(UpdateBookingFormula({ id: this.formula.pkey, payload }))
    } else {
      this.store.dispatch(CreateBookingFormula({ payload }))
    }
    this.close()
  }
}
